import React, { useCallback, useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import '../assets/css/signature-pad.css';

// 署名用キャンバス。写真使用許可・秘密保持誓約・再署名の各画面で共用する。
const SignaturePad = forwardRef(function SignaturePad({ className, label = '署名' }, ref) {
  const canvasRef = useRef(null);

  // 表示サイズに合わせて描画バッファを追従させる。
  // 書面本文はAPIから遅れて届くため、マウント時のサイズで固定すると解像度が崩れる。
  const syncSize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));
    if (canvas.width === width && canvas.height === height) return;

    // 記入済みの署名を保持したままバッファサイズを変更する
    const snapshot = canvas.width > 1 && canvas.height > 1 ? canvas.toDataURL() : null;
    canvas.width = width;
    canvas.height = height;
    if (snapshot) {
      const image = new Image();
      image.onload = () => {
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx) ctx.drawImage(image, 0, 0, width, height);
      };
      image.src = snapshot;
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    syncSize();

    const ctx = canvas.getContext('2d');
    let drawing = false;

    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const point = e.touches ? e.touches[0] : e;
      return { x: (point.clientX - rect.left) * scaleX, y: (point.clientY - rect.top) * scaleY };
    };

    const handleStart = (e) => {
      drawing = true;
      const pos = getPos(e);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    };

    const handleMove = (e) => {
      if (!drawing) return;
      e.preventDefault();
      const pos = getPos(e);
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#000';
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    };

    const handleEnd = () => {
      drawing = false;
    };

    canvas.addEventListener('mousedown', handleStart);
    canvas.addEventListener('mousemove', handleMove);
    canvas.addEventListener('mouseup', handleEnd);
    canvas.addEventListener('mouseout', handleEnd);
    canvas.addEventListener('touchstart', handleStart, { passive: false });
    canvas.addEventListener('touchmove', handleMove, { passive: false });
    canvas.addEventListener('touchend', handleEnd);

    // コールバック内で直接リサイズすると ResizeObserver がループ警告を出すため、次フレームに逃がす
    let frame = 0;
    const scheduleSync = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        syncSize();
      });
    };

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleSync) : null;
    if (observer) observer.observe(canvas);
    window.addEventListener('resize', scheduleSync);

    return () => {
      canvas.removeEventListener('mousedown', handleStart);
      canvas.removeEventListener('mousemove', handleMove);
      canvas.removeEventListener('mouseup', handleEnd);
      canvas.removeEventListener('mouseout', handleEnd);
      canvas.removeEventListener('touchstart', handleStart);
      canvas.removeEventListener('touchmove', handleMove);
      canvas.removeEventListener('touchend', handleEnd);
      if (observer) observer.disconnect();
      window.removeEventListener('resize', scheduleSync);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [syncSize]);

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
  };

  useImperativeHandle(ref, () => ({
    clear,
    isEmpty() {
      const canvas = canvasRef.current;
      if (!canvas) return true;
      const pixels = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height).data;
      for (let i = 3; i < pixels.length; i += 4) {
        if (pixels[i] > 0) return false;
      }
      return true;
    },
    toDataURL() {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }));

  return (
    <div>
      <label htmlFor="signature" className="form-label signature-pad-label">{label}</label>
      <canvas
        id="signature"
        ref={canvasRef}
        className={`signature-pad-canvas ${className || ''}`.trim()}
      ></canvas>
      <button type="button" className="btn btn-secondary btn-sm mt-2" onClick={clear}>
        署名をクリア
      </button>
    </div>
  );
});

export default SignaturePad;
